import React, { useState, useEffect } from 'react';
import { useAuth, axiosInstance as axios } from '../../context/AuthContext';
import { Copy, Check, Send, X, Link2, Mail, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export default function ReferFamilyModal({ isOpen, onClose, families }) {
  const { user } = useAuth();
  const [selectedFamily, setSelectedFamily] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);

  useEffect(() => {
    if (isOpen && families?.length > 0 && !selectedFamily) {
      setSelectedFamily(families[0].family_id);
    }
  }, [isOpen, families, selectedFamily]);

  useEffect(() => {
    if (selectedFamily) {
      fetchReferralCode(selectedFamily);
    }
  }, [selectedFamily]);

  const fetchReferralCode = async (familyId) => {
    setLoadingCode(true);
    try {
      const res = await axios.get(`${API}/family-referrals/code/${familyId}`, { withCredentials: true });
      setReferralCode(res.data.code);
      setReferralLink(res.data.referral_link);
    } catch (err) {
      console.error('Error fetching referral code:', err);
    } finally {
      setLoadingCode(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!recipientEmail.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/family-referrals/invite`, {
        family_id: selectedFamily,
        recipient_email: recipientEmail.trim(),
        recipient_name: recipientName.trim() || null,
        message: message.trim() || null,
      }, { withCredentials: true });
      toast.success('Invitation sent!');
      setRecipientEmail('');
      setRecipientName('');
      setMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const activeFamily = families?.find(f => f.family_id === selectedFamily);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center px-4 z-50" data-testid="refer-family-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-5 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Users className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Refer a Family</h2>
              <p className="text-teal-100 text-sm">Earn 20% off for each family that joins</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1" data-testid="close-refer-modal">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Family selector */}
          {families?.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Referring as</label>
              <select
                value={selectedFamily}
                onChange={(e) => setSelectedFamily(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                data-testid="family-select"
              >
                {families.filter(f => !f.is_archived).map(f => (
                  <option key={f.family_id} value={f.family_id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Referral Link */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <Link2 size={14} />
              Your Referral Link
            </label>
            {loadingCode ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                <Loader2 className="animate-spin" size={16} /> Generating link...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={referralLink}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 truncate"
                  data-testid="referral-link-input"
                />
                <button
                  onClick={handleCopy}
                  className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-1.5 transition ${
                    copied
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                  data-testid="copy-link-btn"
                >
                  {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">Share this link with anyone — when they sign up and create a family, yours gets 20% off!</p>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-gray-400 uppercase tracking-wide">or send an email invite</span>
            </div>
          </div>

          {/* Email Invite Form */}
          <form onSubmit={handleSendInvite} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Their Name</label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Optional"
                  data-testid="recipient-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Their Email *</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="friend@email.com"
                  required
                  data-testid="recipient-email-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Personal Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                rows={2}
                placeholder="Hey! You should check out Legacy Forge..."
                data-testid="invite-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={sending || !recipientEmail.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-lg hover:from-teal-700 hover:to-emerald-700 font-medium text-sm disabled:opacity-50 transition"
              data-testid="send-invite-btn"
            >
              {sending ? (
                <><Loader2 className="animate-spin" size={16} /> Sending...</>
              ) : (
                <><Send size={16} /> Send Invite</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
